import { escapeHTML, formatUSDC, shortValue, showStatus } from "./ui-utils";

type ApiClient = <T>(path: string, options?: RequestInit) => Promise<T>;
type Product = { id: string; name: string };
type ProviderOrder = {
  id: string;
  clientRef?: string;
  payerWallet?: string;
  status: string;
  paymentState: string;
  serviceState: string;
  quote: {
    name: string;
    priceUsdcAtomic: string;
    costUsdcAtomic: string;
    providerPayableAmountAtomic?: string;
  };
  createdAt: string;
};
type Pagination = { page: number; pageSize: number; total: number; totalPages: number };
type Summary = {
  totalCalls: number;
  deliveredCalls: number;
  failedCalls: number;
  settledOrders: number;
  totalRevenueAtomic: string;
  totalCostAtomic: string;
};

function localDateBoundary(value: string, endOfDay: boolean): string {
  const [year, month, day] = value.split("-").map(Number);
  const boundary = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day);
  return boundary.toISOString();
}

export function initializeOrderConsole(api: ApiClient) {
  let pagination: Pagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
  let products: Product[] = [];
  let orders: ProviderOrder[] = [];
  const productFilter = document.querySelector<HTMLSelectElement>("#provider-order-product")!;
  const statusFilter = document.querySelector<HTMLSelectElement>("#provider-order-status")!;
  const dateFrom = document.querySelector<HTMLInputElement>("#provider-order-date-from")!;
  const dateTo = document.querySelector<HTMLInputElement>("#provider-order-date-to")!;
  const pageSize = document.querySelector<HTMLSelectElement>("#provider-order-page-size")!;
  const rows = document.querySelector<HTMLElement>("#provider-order-rows")!;

  function renderProducts(): void {
    const selected = productFilter.value;
    productFilter.innerHTML = `<option value="">全部产品</option>${products.map(product =>
      `<option value="${escapeHTML(product.id)}">${escapeHTML(product.name)}</option>`,
    ).join("")}`;
    productFilter.value = selected;
  }

  function renderOrders(summary: Summary): void {
    document.querySelector<HTMLElement>("#provider-order-empty")!.hidden = orders.length !== 0;
    rows.innerHTML = orders.map(order => {
      const providerPayable = order.quote.providerPayableAmountAtomic || order.quote.costUsdcAtomic || "0";
      return `<tr>
        <td><strong>${escapeHTML(shortValue(order.id, 10))}</strong><small>${escapeHTML(order.quote.name)}${order.clientRef ? ` · ${escapeHTML(order.clientRef)}` : ""}</small></td>
        <td><strong>${order.payerWallet ? escapeHTML(shortValue(order.payerWallet, 8)) : "未识别"}</strong><small>${order.payerWallet ? escapeHTML(order.payerWallet) : "付款完成后记录客户钱包"}</small></td>
        <td><strong>${escapeHTML(formatUSDC(order.quote.priceUsdcAtomic))} USDC</strong><small>Provider 应付 ${escapeHTML(formatUSDC(providerPayable))} USDC</small></td>
        <td><span class="status-chip">${escapeHTML(order.paymentState)}</span></td>
        <td><span class="status-chip">${escapeHTML(order.serviceState)}</span><small>${escapeHTML(order.status)}</small></td>
        <td><code>${escapeHTML(new Date(order.createdAt).toLocaleString("zh-CN"))}</code></td>
      </tr>`;
    }).join("");
    document.querySelector("#provider-order-page-label")!.textContent =
      `第 ${pagination.page} / ${Math.max(1, pagination.totalPages)} 页 · 共 ${pagination.total} 笔`;
    document.querySelector<HTMLButtonElement>("#provider-order-prev")!.disabled = pagination.page <= 1;
    document.querySelector<HTMLButtonElement>("#provider-order-next")!.disabled =
      pagination.totalPages === 0 || pagination.page >= pagination.totalPages;
    document.querySelector("#provider-order-total")!.textContent = String(summary.totalCalls);
    document.querySelector("#provider-order-delivered")!.textContent = String(summary.deliveredCalls);
    document.querySelector("#provider-order-failed")!.textContent = String(summary.failedCalls);
    document.querySelector("#provider-order-settled")!.textContent = String(summary.settledOrders);
    document.querySelector("#provider-order-gross")!.textContent = formatUSDC(summary.totalRevenueAtomic);
    document.querySelector("#provider-order-payable")!.textContent = formatUSDC(summary.totalCostAtomic);
    document.querySelector("#summary-count")!.textContent = `${pagination.total} ORDERS`;
  }

  async function load(page = pagination.page): Promise<void> {
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: pageSize.value });
      if (productFilter.value) query.set("productId", productFilter.value);
      if (statusFilter.value) query.set("status", statusFilter.value);
      if (dateFrom.value) query.set("dateFrom", localDateBoundary(dateFrom.value, false));
      if (dateTo.value) query.set("dateTo", localDateBoundary(dateTo.value, true));
      const body = await api<{ orders: ProviderOrder[]; pagination: Pagination; summary: Summary }>(
        `/v1/provider/orders?${query}`,
      );
      orders = body.orders;
      pagination = body.pagination;
      renderOrders(body.summary);
      document.querySelector<HTMLElement>("#provider-order-status-message")!.hidden = true;
    } catch (error) {
      showStatus("provider-order-status-message", error instanceof Error ? error.message : "订单加载失败", true);
    }
  }

  async function loadProducts(): Promise<void> {
    const body = await api<{ products: Product[] }>("/v1/provider/products");
    products = body.products;
    renderProducts();
  }

  document.querySelector<HTMLFormElement>("#provider-order-filters")!.addEventListener("submit", event => {
    event.preventDefault();
    if (dateFrom.value && dateTo.value && dateFrom.value > dateTo.value) {
      dateTo.setCustomValidity("结束日期不能早于开始日期");
      dateTo.reportValidity();
      return;
    }
    dateTo.setCustomValidity("");
    void load(1);
  });
  dateFrom.addEventListener("input", () => dateTo.setCustomValidity(""));
  dateTo.addEventListener("input", () => dateTo.setCustomValidity(""));
  document.querySelector("#provider-order-prev")!.addEventListener("click", () => {
    if (pagination.page > 1) void load(pagination.page - 1);
  });
  document.querySelector("#provider-order-next")!.addEventListener("click", () => {
    if (pagination.page < pagination.totalPages) void load(pagination.page + 1);
  });

  return { load: async (): Promise<void> => { await Promise.all([loadProducts(), load(1)]); } };
}
